/**
 * Layer 0 — leaf helpers (geometry math, owner/wall matching, rejection tally, loft keep-outs).
 *
 * Part of the dorm-optimizer-v2 placement engine. Split out of the former
 * monolithic placement.js (move-only refactor — bodies verbatim).
 */

const {
  calculateCorners,
  doPolygonsOverlap,
  isPointInsideOrOnPolygon,
  distanceToLineSegment,
} = require('../../bathroom-analyzer/pipeline/geometry');
const { getWallAxes } = require('../../kitchen-assembly/wallMath');
const {
  getDimsMeters,
  getItemDims,
  IN_TO_M,
  getItemDimsInches,
  LOFT_EDGE_INSET_INCHES,
  isLoftBed,
} = require('../constants');

// Project a placed item's physicalCorners onto its wall axis and record the
// occupied [start, end] interval (clamped to [0, wall.length]) in
// occupiedSegments[wallIdx]. No-op when the wall is missing or lacks axes.
// The single source of truth for "reserve this item's span on its wall" — used
// by fixed-bed pre-population and fixed-obstacle injection alike.
function projectFootprintToWallSegment(
  item,
  wallIdx,
  roomData,
  occupiedSegments
) {
  const wall = roomData.walls[wallIdx];
  if (!wall) return;
  const axes = getWallAxes(wall);
  if (!axes) return;
  const { wallDir } = axes;
  let minP = Infinity;
  let maxP = -Infinity;
  for (const c of item.physicalCorners) {
    const cdx = c.x - wall.start.x;
    const cdz = c.z - wall.start.z;
    const p = cdx * wallDir.x + cdz * wallDir.z;
    if (p < minP) minP = p;
    if (p > maxP) maxP = p;
  }
  if (!occupiedSegments[wallIdx]) occupiedSegments[wallIdx] = [];
  occupiedSegments[wallIdx].push({
    start: Math.max(0, minP),
    end: Math.min(wall.length, maxP),
  });
}

// Rotation (radians) that aligns an object's local +Z axis with the 2D
// direction `dir` — i.e. makes the object "face" along `dir`. Local +Z points
// along a wall's inward normal (see web-app/CLAUDE.md), so this is the single
// canonical "which way does this piece face" conversion. `dir` is {x, z} and
// need not be unit length. The -x/+z sign convention is error-prone by hand
// (it's the source of facing bugs), so it lives here once.
function facingRotation(dir) {
  return Math.atan2(-dir.x, dir.z);
}

// True if `corners` overlaps any door-swing arc. `doorSwingArcs` may be null/
// empty (no doors), in which case nothing overlaps. The single canonical
// "does this candidate block a door?" check — the caller decides what to do
// with the result (continue / return null / tally).
function overlapsAnyDoorArc(corners, doorSwingArcs) {
  return (
    !!doorSwingArcs &&
    doorSwingArcs.some((arc) => doPolygonsOverlap(corners, arc))
  );
}

// How far outside the inner boundary a corner may sit and still count as
// contained. Exported because a caller that pre-filters poses on its own
// arithmetic (center-divider's endGapsAreWalkable) must use the SAME slack, or
// it rejects poses this function would have accepted.
const BOUNDARY_TOL_M = 0.01;

// True if every corner sits inside (or within `tol` of) the room's inner
// boundary — i.e. the footprint is contained in the wall-shrunk floor. The
// caller decides the failure action (continue / return null) and any tally.
function cornersInsideBoundary(corners, roomData, tol = BOUNDARY_TOL_M) {
  return corners.every((c) =>
    isPointInsideOrOnPolygon(c, roomData.innerBoundary, tol)
  );
}

/**
 * A placed item's footprint edges as {a, b, len}, in corner order.
 *
 * `minLen` drops degenerate edges; pass 0 to keep all four.
 *
 * The loop is three lines, which is exactly why it had been written out three
 * times (getLoftLadderZones, getUnderBedPositions, placeAgainstFurniture) —
 * and two of those had grown DIFFERENT ideas of which edge is "short".
 */
function footprintSides(corners, minLen = 0) {
  const sides = [];
  if (!corners || corners.length < 3) return sides;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (len < minLen) continue;
    sides.push({ a, b, len });
  }
  return sides;
}

/**
 * True when `len` matches the shorter of an item's two footprint axes — the
 * head/foot of a bed, the end of a desk.
 *
 * Compared against `dims` rather than against the other measured edges: a
 * relative test (`len >= 0.8 * longestEdge`) calls BOTH axes long on a nearly
 * square footprint, which is the opposite of what a caller filtering for short
 * sides wants. `dims` here must be the item's PLACED dims — wall placement
 * stamps along/perp-swapped values — so read it off the placed record, never
 * off the catalog.
 */
function isShortSide(len, dims, tol = 0.05) {
  if (!dims) return false;
  return Math.abs(len - Math.min(dims.w, dims.d)) < tol;
}

function edgeAbutsWall(a, b, walls) {
  const tolerance = 0.15;
  for (const wall of walls) {
    const dA = distanceToLineSegment(a, wall.start, wall.end);
    const dB = distanceToLineSegment(b, wall.start, wall.end);
    if (dA < tolerance && dB < tolerance) return true;
  }
  return false;
}

// Center for a surface item (bookshelf, microwave, …) seated on a host
// (dresser/desk). The item is pushed toward the host's back face — the wall —
// by half the depth difference so its back sits flush against the wall, and
// optionally shifted along the host's width by `widthOffset` (for stacking
// multiple items across one surface). `host.rotation` follows the wall-back
// convention (atan2(-inward.x, inward.z)), so (sin, -cos) points toward the wall.
function surfaceCenterOnHost(host, itemDims, widthOffset = 0) {
  const hostDims = host.dims;
  const depthOffset = (hostDims.d - itemDims.d) / 2;
  return {
    x:
      host.center.x +
      Math.sin(host.rotation) * depthOffset +
      Math.cos(host.rotation) * widthOffset,
    z:
      host.center.z -
      Math.cos(host.rotation) * depthOffset +
      Math.sin(host.rotation) * widthOffset,
  };
}

/**
 * Stable identity for a surface HOST (a dresser/desk something sits on).
 * Type alone is ambiguous once two people own the same furniture, so the id
 * pins the placed instance by position. Used both to track per-host surface
 * occupancy and, once stamped on the surface item, to recover the host's real
 * height when emitting the Firestore contract.
 */
function surfaceHostId(host) {
  return `${host.typeKey}:${host.center.x.toFixed(3)},${host.center.z.toFixed(3)}`;
}

function sortByFootprintDesc(items) {
  return [...items].sort((a, b) => {
    // Real footprint, not the catalog default — a custom-enlarged item must
    // sort as the big item it is, or it gets placed last into leftover space.
    const da = getItemDimsInches(a);
    const db = getItemDimsInches(b);
    if (!da || !db) return 0;
    return db.w * db.d - da.w * da.d;
  });
}

/**
 * Top-level orchestrator: resolves bed configs, generates furniture
 * archetypes, and produces scored placement candidates.
 */
function bedPlacementKey(beds) {
  return beds
    .map(
      (b) =>
        `${b.center.x.toFixed(3)},${b.center.z.toFixed(3)},${b.rotation.toFixed(3)},${b.bedType || 'standard'}`
    )
    .sort()
    .join('|');
}

/**
 * Resolves semantic align values ('near-window', 'near-door') to
 * concrete 'start'/'end'/'center'.
 */
function resolveAlign(align, wallIdx, roomData, wallRoles) {
  if (align === 'start' || align === 'end' || align === 'center') return align;

  const roleMap = {
    'near-window': 'window',
    'near-door': 'door',
    'near-left': 'left',
    'near-right': 'right',
  };
  const refRole = roleMap[align];
  if (!refRole) return 'start';
  const refWallIdx = wallRoles[refRole];
  if (refWallIdx == null) return 'start';

  const wall = roomData.walls[wallIdx];
  const refWall = roomData.walls[refWallIdx];
  const refMid = {
    x: (refWall.start.x + refWall.end.x) / 2,
    z: (refWall.start.z + refWall.end.z) / 2,
  };

  const distStart = Math.hypot(
    wall.start.x - refMid.x,
    wall.start.z - refMid.z
  );
  const distEnd = Math.hypot(wall.end.x - refMid.x, wall.end.z - refMid.z);
  return distStart <= distEnd ? 'start' : 'end';
}

// Greedy bipartite core: match each owner to a distinct wall "group" by
// proximity of the owner's bed to the group's anchor wall. `owners[i]` is an
// ownerId; `anchorWalls[g]` is the wall index representing group g. Returns a
// permutation `perm` where perm[i] = the group index assigned to owner i
// (owners with no bed / unmatched keep their own index). Returns null when there
// is nothing to reorder (<2 owners/anchors, or <2 distinct anchor walls).
function matchOwnersToWalls(owners, anchorWalls, placedBeds, walls) {
  if (owners.length < 2 || anchorWalls.length < 2) return null;
  if (new Set(anchorWalls).size < 2) return null;

  const wallMid = (wallIdx) => {
    const w = walls[wallIdx];
    if (!w) return null;
    return { x: (w.start.x + w.end.x) / 2, z: (w.start.z + w.end.z) / 2 };
  };
  const bedByOwner = new Map();
  for (const bed of placedBeds) {
    if (bed.typeKey === 'dorm-bed' && bed.ownerId) {
      bedByOwner.set(bed.ownerId, bed);
    }
  }

  const pairs = [];
  for (let oi = 0; oi < owners.length; oi++) {
    const bed = bedByOwner.get(owners[oi]);
    if (!bed) continue;
    for (let gi = 0; gi < anchorWalls.length; gi++) {
      const mid = wallMid(anchorWalls[gi]);
      if (!mid) continue;
      const dist = Math.hypot(bed.center.x - mid.x, bed.center.z - mid.z);
      pairs.push({ oi, gi, dist });
    }
  }
  pairs.sort((a, b) => a.dist - b.dist);

  const perm = new Array(owners.length).fill(null);
  const usedOwners = new Set();
  const usedGroups = new Set();
  for (const { oi, gi } of pairs) {
    if (usedOwners.has(oi) || usedGroups.has(gi)) continue;
    perm[oi] = gi;
    usedOwners.add(oi);
    usedGroups.add(gi);
  }
  for (let oi = 0; oi < owners.length; oi++) {
    if (perm[oi] == null) perm[oi] = oi;
  }
  return perm;
}

// One wall per desk → reassign each desk's wall to the one nearest its owner's
// bed. (Thin wrapper over matchOwnersToWalls, groupSize 1.)
function assignDesksToOwnerSide(desks, deskWallIdx, placedBeds, walls) {
  if (!deskWallIdx || desks.length < 2 || deskWallIdx.length < 2) {
    return deskWallIdx;
  }
  const perm = matchOwnersToWalls(
    desks.map((d) => d.ownerId),
    deskWallIdx,
    placedBeds,
    walls
  );
  if (!perm) return deskWallIdx;
  return desks.map((_, di) => deskWallIdx[perm[di]] ?? deskWallIdx[0]);
}

// Storage archetypes lay walls out as [dresser1, wardrobe1, dresser2, wardrobe2]
// — a (dresser, wardrobe) PAIR per owner. Reassign each owner's pair to the
// walls nearest their bed (anchored on the dresser wall) so a wardrobe/dresser
// never lands in a roommate's zone. (Thin wrapper, groupSize 2.)
function assignStorageToOwnerSide(ownerIds, storageWallIdx, placedBeds, walls) {
  if (!storageWallIdx || ownerIds.length < 2 || storageWallIdx.length < 4) {
    return storageWallIdx;
  }
  const anchors = ownerIds.map((_, oi) => storageWallIdx[oi * 2]);
  const perm = matchOwnersToWalls(ownerIds, anchors, placedBeds, walls);
  if (!perm) return storageWallIdx;
  const out = storageWallIdx.slice();
  for (let oi = 0; oi < ownerIds.length; oi++) {
    const gi = perm[oi];
    out[oi * 2] = storageWallIdx[gi * 2];
    out[oi * 2 + 1] = storageWallIdx[gi * 2 + 1];
  }
  return out;
}

// ─── Per-archetype orchestrator ─────────────────────────────────────

/**
 * Try to place a single item against a wall.
 *
 * @param {string}  typeKey
 * @param {string|null} ownerId
 * @param {number}  wallIdx
 * @param {Object}  roomData
 * @param {Object}  occupiedSegments  — mutated on success
 * @param {Object[]} placedItems      — read-only collision check
 * @param {string}  [orientation]     — 'long-side' | 'short-side' (beds only)
 * @param {boolean} [preferEnd]       — try end-aligned gaps first
 * @returns {PlacedItem|null}
 */
// ─── Placement diagnostics (optional, read-only) ────────────────────
// When `_rejectionTally` is a non-null object, placeOnWall tallies why each
// candidate position was rejected, keyed by reason. Module-scoped on purpose:
// this avoids threading a collector through placeOnWall + every placeBestWall /
// placeBestWallRotated call site, and it aggregates rejections across all the
// walls a single item is tried against. Default null → every tally is a no-op,
// so non-debug callers are unaffected. Use beginRejectionTally / endRejectionTally
// (save/restore) around a placement attempt to capture one item's reasons.
let _rejectionTally = null;

function tallyRejection(reason) {
  if (_rejectionTally) {
    _rejectionTally[reason] = (_rejectionTally[reason] || 0) + 1;
  }
}

function beginRejectionTally() {
  const prev = _rejectionTally;
  _rejectionTally = {};
  return prev;
}

function endRejectionTally(prev) {
  const tally = _rejectionTally;
  _rejectionTally = prev;
  return tally;
}

// Summarize a tally into a compact string, dominant reason first, e.g.
// "clearance ×4, door-swing ×2". Returns null when nothing was tallied.
function summarizeRejections(tally) {
  if (!tally) return null;
  const entries = Object.entries(tally).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return entries.map(([reason, n]) => `${reason} ×${n}`).join(', ');
}

// Build the two short-end keep-out strips for a loft bed (ladder/frame-post
// zones). Each strip sits past one end of the longer footprint axis, extends
// LOFT_EDGE_INSET_INCHES outward, and overlaps ~2" back into the footprint so
// items flush against a short end still register. Returns an array of two
// polygons (each a 4-corner array), matching the `overlapsLoftKeepout` reader.
function buildLoftShortEndKeepout(bed) {
  const loftInset = LOFT_EDGE_INSET_INCHES * IN_TO_M;
  const overlap = 0.05; // ~2" back into the footprint
  const stripLen = loftInset + overlap;
  const off = (loftInset - overlap) / 2; // strip center offset past the end face
  const cos = Math.cos(bed.rotation);
  const sin = Math.sin(bed.rotation);
  const { w, d } = bed.dims;
  // Long axis carries the short ends. width arg → (cos,sin) axis; depth arg →
  // (-sin,cos) axis (see calculateCorners).
  const wide = w >= d;
  const dir = wide ? { x: cos, z: sin } : { x: -sin, z: cos };
  const halfLong = (wide ? w : d) / 2 + off;
  return [1, -1].map((s) => {
    const c = {
      x: bed.center.x + dir.x * halfLong * s,
      z: bed.center.z + dir.z * halfLong * s,
    };
    return wide
      ? calculateCorners(c, stripLen, d, bed.rotation)
      : calculateCorners(c, w, stripLen, bed.rotation);
  });
}

// True if `corners` overlaps either loft short-end keep-out strip. `keepout`
// is the array produced by buildLoftShortEndKeepout (or null/undefined).
/**
 * Does this placed item take part in FLOOR clearance — either carrying a
 * clearance zone another body can block, or having a body that can block one?
 *
 * The single definition behind THREE parties, not two:
 *   - `clearanceConflictItems` (validation.js) — the post-pass that DELETES a
 *     conflicting EXTENDED item;
 *   - the clearance gate in `placeOnWall`;
 *   - `validateLayout`'s C7 (validation.js), which is the arbiter for BASE
 *     furniture. Its own `other` set equals this predicate MODULO the corners
 *     guard below — C7 has no `length >= 3` check, so a null-cornered
 *     `unplaced` / `desk-zone` record is in C7's set and out of this one, which
 *     is harmless only because `doPolygonsOverlap` tolerates a null.
 *     `placeOnWall` is shared by the bed/base placers, so the gate below
 *     governs base layouts too, and is correct there because of that
 *     near-equality. Change C7 and this predicate together.
 *
 * Placer and post-pass must agree exactly: a placer looser than the post-pass
 * places items that are then deleted, and a placer stricter than it refuses
 * positions the post-pass would have allowed. Both have happened.
 *
 * NOTE this is an EXCLUSION list — any placement value not named here counts as
 * a floor item, in every one of the three parties above. A new placement kind
 * therefore opts IN by default; `placementValues.ci.test.js` pins the produced
 * set so a new one cannot be added silently.
 *
 * Under-bed and surface items legitimately share footprints (an under-loft desk
 * sits under a bed). Wall- and door-mounted decor hangs at height — it neither
 * carries floor clearance nor blocks anyone else's (a chair slides under a wall
 * hook). The loft short-end keep-out is NOT a clearance zone and is deliberately
 * outside this rule; it is enforced against every item regardless.
 */
function participatesInFloorClearance(item) {
  return !!(
    item &&
    item.physicalCorners &&
    item.physicalCorners.length >= 3 &&
    item.placement !== 'under-bed' &&
    item.placement !== 'surface' &&
    item.placement !== 'wall-mounted' &&
    item.placement !== 'door-mounted'
  );
}

/**
 * Whether `body` stands in the access zone of `owner`, an item in the slot
 * along a bed (placement 'under-bed') — a captain's dresser, an under-loft
 * desk, a bookshelf at a bed's open side.
 *
 * `participatesInFloorClearance` leaves under-bed items out, and for their
 * BODIES that is right: they sit inside the bed's outline, not on open floor.
 * But their access zone IS open floor — the drawers of a captain's dresser pull
 * out in front of the bed — and leaving the item out dropped the zone with it,
 * so a wardrobe could be stood in it and neither the placer nor C7 noticed.
 * This is the zone half on its own, applied ADDITIVELY by the same three
 * parties (placeOnWall, C7, clearanceConflictItems): everything the predicate
 * already counts is untouched.
 *
 * The zone starts at the item's back, inside its host bed, so the host bed is
 * never a blocker.
 */
function bodyInUnderBedAccessZone(owner, body) {
  if (!owner || owner.placement !== 'under-bed') return false;
  const zone = owner.clearanceCorners;
  if (!Array.isArray(zone) || zone.length < 3) return false;
  const corners = body && body.physicalCorners;
  if (!Array.isArray(corners) || corners.length < 3) return false;
  if (!participatesInFloorClearance(body)) return false;
  if (
    body.typeKey === 'dorm-bed' &&
    isPointInsideOrOnPolygon(owner.center, corners, 0.02)
  ) {
    return false;
  }
  return doPolygonsOverlap(zone, corners);
}

function overlapsLoftKeepout(corners, keepout) {
  if (!keepout) return false;
  for (const strip of keepout) {
    if (doPolygonsOverlap(corners, strip)) return true;
  }
  return false;
}

// The loft bed whose footprint covers `host`'s center, or null. Used to treat
// a desk/dresser packed under a loft as a valid attachment host (its exposed
// room-facing side is usable even though its placement is 'under-bed').
function hostUnderLoft(host, placedItems) {
  for (const bed of placedItems) {
    if (
      bed.typeKey === 'dorm-bed' &&
      isLoftBed(bed.bedType) &&
      bed.physicalCorners &&
      isPointInsideOrOnPolygon(host.center, bed.physicalCorners, 0.02)
    ) {
      return bed;
    }
  }
  return null;
}

/**
 * The synthetic record for an item that is in the layout's inventory but has no
 * position — either it found no spot, or it was demoted. It scores 0
 * (self-penalizing) so a real placement always wins, and finalist re-scoring
 * re-ranks with it included.
 *
 * `center` is the room ORIGIN rather than null because several consumers key
 * layouts on center; scoring rules must therefore filter on `placement` before
 * reading it (see placedOfType in scoring-helpers.js).
 *
 * `placement` is a parameter because the desk-zone phase pushes the identical
 * record under a different label — it isn't unplaced, it just isn't spatial.
 *
 * @param {Object} item                     carries typeKey / ownerId / dims
 * @param {Object} [opts]
 * @param {string} [opts.placement]         'unplaced' (default) or 'desk-zone'
 * @param {string} [opts.unplacedReason]    dominant reason, surfaced to the UI
 * @param {string} [opts.placedVia]         which cascade stage ran, for debug
 */
function unplacedEntry(item, opts = {}) {
  const {
    placement = 'unplaced',
    unplacedReason = null,
    placedVia = null,
  } = opts;
  return {
    typeKey: item.typeKey,
    ownerId: item.ownerId,
    center: { x: 0, z: 0 },
    rotation: 0,
    dims: getItemDims(item),
    physicalCorners: null,
    clearanceCorners: null,
    placement,
    placedVia,
    unplacedReason,
    wallIdx: null,
  };
}

/**
 * Owner identity for matching an expected item against a placed one. A bunk bed
 * carries `ownerIds` (shared by two occupants) instead of `ownerId`.
 */
function ownerKey(item) {
  return item.ownerId ?? (item.ownerIds || []).join('+');
}

/**
 * Which of `expected` never made it into `placed`, matched one-for-one on
 * (typeKey, owner). A multiset diff rather than a count comparison, because the
 * caller needs to know WHICH piece is missing to report it.
 *
 * @param {Object[]} expected  item descriptors the layout is supposed to contain
 * @param {Object[]} placed    the items it actually contains
 * @returns {Object[]} subset of `expected`
 */
function missingItems(expected, placed) {
  const remaining = placed.slice();
  const missing = [];
  for (const want of expected) {
    const idx = remaining.findIndex(
      (it) => it.typeKey === want.typeKey && ownerKey(it) === ownerKey(want)
    );
    if (idx === -1) missing.push(want);
    else remaining.splice(idx, 1);
  }
  return missing;
}

// The per-occupant furniture a complete layout must contain. Bookshelves are
// deliberately absent — they have their own strategy fan-out and are allowed to
// go unplaced.
const EXPECTED_BASE_TYPES = new Set([
  'dorm-bed',
  'dorm-desk',
  'dorm-dresser',
  'dorm-wardrobe',
]);

/**
 * Which pieces of the student's base furniture this layout failed to place.
 *
 * Count the expected TYPES explicitly: `baseItems` also carries the injected
 * fixed obstacles and, on the under-loft variants, packed personal items, so
 * counting "everything that isn't a bookshelf" lets those stand in for a
 * missing wardrobe — which is how a room with a built-in once shipped layouts
 * quietly short a piece of the user's own furniture.
 *
 * Shared by the bed-config generator and the refit re-solve. Both must gate on
 * completeness identically; refit had no gate at all and could report
 * "everything fits" with a wardrobe simply absent from the item list, since a
 * piece that was never placed has no 'unplaced' entry to count.
 *
 * @param {Object[]} baseItems   what the layout actually contains
 * @param {Object[]} fixedBeds   the beds this layout was built around
 * @param {Object}   classified  classifyItems() output (desks/dressers/wardrobes)
 * @returns {Object[]} the expected items that are missing (empty when complete)
 */
function missingBaseItems(baseItems, fixedBeds, classified) {
  const expected = [
    ...fixedBeds,
    ...classified.desks,
    ...classified.dressers,
    ...classified.wardrobes,
  ];
  const placed = baseItems.filter(
    (it) => !it.isFixed && EXPECTED_BASE_TYPES.has(it.typeKey)
  );
  if (placed.length >= expected.length) return [];
  return missingItems(expected, placed);
}

module.exports = {
  surfaceHostId,
  unplacedEntry,
  missingItems,
  missingBaseItems,
  EXPECTED_BASE_TYPES,
  assignDesksToOwnerSide,
  assignStorageToOwnerSide,
  bedPlacementKey,
  beginRejectionTally,
  buildLoftShortEndKeepout,
  BOUNDARY_TOL_M,
  cornersInsideBoundary,
  edgeAbutsWall,
  footprintSides,
  isShortSide,
  endRejectionTally,
  facingRotation,
  hostUnderLoft,
  overlapsAnyDoorArc,
  overlapsLoftKeepout,
  participatesInFloorClearance,
  bodyInUnderBedAccessZone,
  projectFootprintToWallSegment,
  resolveAlign,
  sortByFootprintDesc,
  summarizeRejections,
  surfaceCenterOnHost,
  tallyRejection,
};
